import { useEffect, useState } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link, useOutletContext } from 'react-router-dom';
import { ShieldCheck, Plus, Trash2, RefreshCw, Smartphone, ClipboardList, X, Users, History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { boPath } from '@/utils/getBasePath';
import {
  ROLE_LABELS, listAccess, accessHistory, addAccess, setAccess, removeAccess, formatPhone, detailText, parsePasted,
} from '@/lib/posLoginAccess';
import ApprovalCodesCard from '@/components/approval/ApprovalCodesCard';
import { getApprovalCodes } from '@/lib/approvalCodes';

// POS Login Access (Staff & Labor): the phones allowed to sign in to the POS.
// Only phones on this list get a texted code on the POS; everyone else is told
// to ask the owner. An entry IS the employee's POS access (the Employees page's
// "Allow POS login" switch is the same thing). Owner / Manager entries: only
// the owner adds them, linked to the real login (the POS signs them in with
// that login's permissions). Turning a phone off signs it out everywhere.

const ROLE_STYLE = {
  owner: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  waiter: 'bg-cyan-100 text-cyan-800',
  cashier: 'bg-green-100 text-green-800',
  host: 'bg-pink-100 text-pink-800',
  kitchen: 'bg-orange-100 text-orange-800',
  expo: 'bg-amber-100 text-amber-800',
};

const blankRow = (role = 'waiter') => ({ name: '', phone: '', role, manager_id: '' });

const when = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');

const selectClass = 'h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900';

export default function PosLoginAccessPage() {
  const context = useOutletContext() || {};
  const token = context.token || localStorage.getItem('backoffice_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([blankRow()]);
  const [pasted, setPasted] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lineErrors, setLineErrors] = useState([]);
  const [events, setEvents] = useState(null);
  const [codes, setCodes] = useState(null); // approval codes (owners / managers only; others: 403 -> hidden)

  const load = useLatestCallback(async () => {
    try {
      setData(await listAccess(token));
    } catch (e) {
      toast.error(detailText(e, 'Could not load POS Login Access'));
    } finally {
      setLoading(false);
    }
    getApprovalCodes(token).then(setCodes).catch(() => setCodes(null));
  });

  useEffect(() => { if (token) load(); }, [token, load]);

  const roles = data?.roles || [];
  const logins = data?.logins || [];
  const defaultRole = roles.includes('waiter') ? 'waiter' : roles[roles.length - 1] || 'waiter';
  const entries = data?.entries || [];
  const allowedCount = entries.filter((e) => e.status === 'allowed').length;

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async (list) => {
    const clean = list
      .filter((r) => (r.phone || '').trim())
      .map((r) => ({ name: (r.name || '').trim() || undefined, phone: r.phone.trim(), role: r.role,
        manager_id: r.manager_id ? Number(r.manager_id) : undefined }));
    if (!clean.length) { toast.error('Add at least one phone'); return; }
    setSaving(true);
    try {
      const out = await addAccess(token, clean);
      const failed = out.results.filter((r) => !r.ok);
      setLineErrors(failed.map((r) => `${r.phone}: ${detailText(r.error, 'not added')}`));
      if (out.added) toast.success(`${out.added} phone${out.added === 1 ? '' : 's'} allowed to sign in to the POS`);
      if (failed.length) toast.error(`${failed.length} not added - see below`);
      if (!failed.length) { setRows([blankRow(defaultRole)]); setPasted(''); setShowPaste(false); }
      load();
    } catch (e) {
      toast.error(detailText(e, 'Could not add the phones'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (entry) => {
    const allow = entry.status !== 'allowed';
    try {
      const out = await setAccess(token, entry.id, allow);
      toast.success(allow ? `${entry.name} may sign in to the POS`
        : `${entry.name} is off the POS (${out.devices_revoked} device${out.devices_revoked === 1 ? '' : 's'} signed out)`);
      load();
    } catch (e) {
      toast.error(detailText(e, 'Could not change this phone'));
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove ${entry.name} (${formatPhone(entry.phone)}) from POS Login Access? `
      + 'They are signed out of the POS on every device.')) return;
    try {
      await removeAccess(token, entry.id);
      toast.success(`${entry.name} removed from POS Login Access`);
      load();
    } catch (e) {
      toast.error(detailText(e, 'Could not remove this phone'));
    }
  };

  const showHistory = async () => {
    try { setEvents(await accessHistory(token)); } catch (e) { toast.error(detailText(e, 'Could not load the history')); }
  };

  const nameOf = (ref) => {
    const [kind, id] = String(ref || '').split(':');
    if (kind === 'manager') return logins.find((l) => String(l.id) === id)?.name || 'Account login';
    return entries.find((e) => String(e.id) === id)?.name || 'Staff';
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="pos-login-access-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900 md:text-2xl flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-600" /> POS Login Access
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Only these phones can sign in to the POS. The first sign-in texts a code, then they set their PIN.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild className="min-h-[44px]">
            <Link to={boPath('employees')}><Users className="w-4 h-4 mr-2" />Employees</Link>
          </Button>
          <Button variant="outline" onClick={load} className="min-h-[44px]" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {data?.can_write && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" />Allow phones</CardTitle>
            <CardDescription>
              A phone that matches an employee turns on their POS login; a new phone adds a POS profile.
              {data?.can_add_owner ? ' Add your own phone as Owner to use the POS with full owner permissions.'
                : ' Only the owner can add owners and managers.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end" data-testid={`access-row-${i}`}>
                <div className="sm:col-span-3">
                  <Label className="text-xs text-slate-600">Name</Label>
                  <Input value={row.name} placeholder={row.role === 'owner' ? 'Your name' : 'Full name'}
                    onChange={(e) => setRow(i, { name: e.target.value })} data-testid={`access-name-${i}`} />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs text-slate-600">Phone</Label>
                  <Input type="tel" value={row.phone} placeholder="(628) 999-8118"
                    onChange={(e) => setRow(i, { phone: e.target.value })} data-testid={`access-phone-${i}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-slate-600">Role</Label>
                  <select className={selectClass} value={row.role} data-testid={`access-role-${i}`}
                    onChange={(e) => setRow(i, { role: e.target.value, manager_id: '' })}>
                    {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  {(row.role === 'owner' || row.role === 'manager') && (
                    <>
                      <Label className="text-xs text-slate-600">Signs in as</Label>
                      <select className={selectClass} value={row.manager_id} data-testid={`access-login-${i}`}
                        onChange={(e) => setRow(i, { manager_id: e.target.value })}>
                        {row.role === 'owner' ? <option value="">My owner login</option>
                          : <option value="">A manager on staff (no login)</option>}
                        {logins.filter((l) => l.role === row.role).map((l) => (
                          <option key={l.id} value={l.id}>{l.name} ({l.email})</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  {rows.length > 1 && (
                    <Button variant="ghost" size="icon" aria-label="Remove row" className="min-w-[44px] min-h-[44px]"
                      onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setRows((rs) => [...rs, blankRow(defaultRole)])} className="min-h-[44px]">
                <Plus className="w-4 h-4 mr-2" />Another phone
              </Button>
              <Button variant="outline" onClick={() => setShowPaste((v) => !v)} className="min-h-[44px]">
                <ClipboardList className="w-4 h-4 mr-2" />Paste a list
              </Button>
              <Button onClick={() => submit(rows)} disabled={saving} className="min-h-[44px]" data-testid="access-save">
                <ShieldCheck className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Allow'}
              </Button>
            </div>
            {showPaste && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-600">One per line: name, phone, role (role optional)</Label>
                <Textarea rows={5} value={pasted} onChange={(e) => setPasted(e.target.value)}
                  placeholder={'Kevin Dole, 628-999-8118, cashier\nAnn Lee, (415) 555-0100, host'} data-testid="access-paste" />
                <Button onClick={() => submit(parsePasted(pasted, defaultRole))} disabled={saving} className="min-h-[44px]">
                  Allow {parsePasted(pasted).length || ''} pasted
                </Button>
              </div>
            )}
            {lineErrors.length > 0 && (
              <ul className="text-sm text-red-700 bg-red-50 rounded-md p-3 space-y-1" data-testid="access-errors">
                {lineErrors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Allowed phones ({allowedCount})</CardTitle>
          <CardDescription>Employees not on this list cannot sign in to the POS.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : entries.length === 0 ? (
            <div className="py-8 text-center text-slate-600 text-sm">No phone is allowed yet. Add yours above as Owner.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {entries.map((e) => (
                <li key={e.id} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`access-entry-${e.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{e.name}</span>
                      <Badge className={ROLE_STYLE[e.role] || 'bg-slate-100 text-slate-800'}>{ROLE_LABELS[e.role] || e.role}</Badge>
                      {e.status === 'allowed'
                        ? <Badge className="bg-green-100 text-green-800">Allowed</Badge>
                        : <Badge variant="outline" className="text-slate-600">Off</Badge>}
                    </div>
                    <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" />{formatPhone(e.phone)}</span>
                      <span>PIN: {e.pin_set ? 'set' : 'not yet'}</span>
                      <span>Trusted devices: {e.trusted_devices}</span>
                      <span>Last POS sign-in: {when(e.last_pos_login_at)}</span>
                    </div>
                  </div>
                  {data?.can_write && (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => toggle(e)} className="min-h-[44px]" data-testid={`access-toggle-${e.id}`}>
                        {e.status === 'allowed' ? 'Turn off' : 'Allow'}
                      </Button>
                      <Button variant="ghost" onClick={() => remove(e)} className="min-h-[44px] text-red-700" aria-label={`Remove ${e.name}`}
                        data-testid={`access-remove-${e.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {codes && <ApprovalCodesCard token={token} data={codes} onChanged={setCodes} />}

      <Card className="border-slate-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" />Changes</CardTitle>
          {events === null && <Button variant="outline" onClick={showHistory} className="min-h-[44px]">Show</Button>}
        </CardHeader>
        {events !== null && (
          <CardContent>
            {events.length === 0 ? <p className="text-sm text-slate-600">No changes yet.</p> : (
              <ul className="text-sm text-slate-700 space-y-1">
                {events.map((ev, i) => (
                  <li key={i}>
                    {when(ev.at)} - {nameOf(ev.by)} {ev.action.replace('pos_access.', '').replace('_', ' ')}
                    {' '}{entries.find((x) => String(x.id) === ev.entry_id)?.name || `entry ${ev.entry_id}`}
                    {ev.after?.phone ? ` (${formatPhone(ev.after.phone)})` : ''}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
